import { H3HexagonLayer } from 'deck.gl';
import {
  PARAMETROS_ADITIVOS,
  brilloPulso,
  celdasConTiempos,
  celdasConAnillos,
} from './pulso';

// Ecos de segundo orden: el mismo pulso viajero de capas/onda.js, pero
// centrado en cada celda donde trabajan los residentes de la elegida, y
// coloreado en rojo para distinguirse del dorado del primer orden.
// Intensidad segun el peso relativo del destino frente al dominante de esa
// celda. Usa tiempos de viaje reales (isocronas.json) cuando estan
// disponibles para ese destino; si no, una cascada topologica mas corta
// (son ecos, no el barrido central).
const ROJO_OSCURO = [140, 24, 18];
const ROJO_CRESTA = [255, 128, 110];
const OPACIDAD_MAXIMA = 220;
const PISO_PESO = 0.27; // brillo minimo para destinos marginales (0 a 1)
const ANCHO_ESTELA_MINUTOS = 3;
const ANCHO_ESTELA_ANILLOS = 1.4;
const MAX_ANILLOS_RESPALDO = 3;
const TRANSPARENTE = [0, 0, 0, 0];

function interpolaColorEco(pesoRelativo) {
  const t = Math.max(0, Math.min(1, pesoRelativo));
  return [
    Math.round(ROJO_OSCURO[0] + (ROJO_CRESTA[0] - ROJO_OSCURO[0]) * t),
    Math.round(ROJO_OSCURO[1] + (ROJO_CRESTA[1] - ROJO_OSCURO[1]) * t),
    Math.round(ROJO_OSCURO[2] + (ROJO_CRESTA[2] - ROJO_OSCURO[2]) * t),
  ];
}

// Celdas de todos los ecos, fijas por seleccion; se memoizan en App. Como en
// la onda del primer orden, la animacion vive en el accessor de color, no en
// el data: regenerarlo por frame re-teselaba los hexagonos de hasta 30
// destinos 60 veces por segundo.
//
// "destinos" es el array [[work_h3, peso], ...] de casa_trabajo.json para la
// celda de residencia elegida (ya truncado a los principales al preparar los
// datos). El peso se normaliza contra el maximo de la propia lista: el
// destino dominante siempre se ve brillante, los marginales se atenuan sin
// desaparecer del todo (PISO_PESO).
export function preparaEcos({ destinos, isocronas, grilla }) {
  if (!destinos || destinos.length === 0) return null;

  const pesoMaximo = Math.max(...destinos.map(([, peso]) => peso));
  const celdas = [];
  for (const [workH3, peso] of destinos) {
    const pesoRelativo = PISO_PESO + (peso / pesoMaximo) * (1 - PISO_PESO);
    const pares = isocronas && isocronas[workH3];
    if (pares) {
      for (const { h3, minutos } of celdasConTiempos(pares, grilla)) {
        celdas.push({ h3, minutos, pesoRelativo });
      }
    } else {
      for (const { h3, anillo } of celdasConAnillos(workH3, MAX_ANILLOS_RESPALDO)) {
        celdas.push({ h3, anillo, pesoRelativo });
      }
    }
  }
  return celdas;
}

export function construyeCapaSegundoOrden({ celdas, t, maxMinutos = 30 }) {
  if (!celdas || celdas.length === 0) return null;

  const frenteAnillos = (t / maxMinutos) * MAX_ANILLOS_RESPALDO;

  return new H3HexagonLayer({
    id: 'segundo-orden',
    data: celdas,
    pickable: false,
    extruded: false,
    filled: true,
    stroked: false,
    highPrecision: 'auto',
    parameters: PARAMETROS_ADITIVOS,
    getHexagon: (d) => d.h3,
    getFillColor: (d) => {
      const brillo =
        d.minutos !== undefined
          ? brilloPulso(t - d.minutos, ANCHO_ESTELA_MINUTOS)
          : brilloPulso(frenteAnillos - d.anillo, ANCHO_ESTELA_ANILLOS);
      if (brillo === null) return TRANSPARENTE;
      return [
        ...interpolaColorEco(d.pesoRelativo),
        Math.round(brillo * d.pesoRelativo * OPACIDAD_MAXIMA),
      ];
    },
    updateTriggers: {
      getFillColor: [t],
    },
  });
}
